import React, { ReactElement, useState } from 'react';
import {
  Pressable, StyleSheet, Text, View,
} from 'react-native';
import { useSelector } from 'react-redux';
import Colors from '../../constants/Colors';
import EllipsisIcon from '../EllipsisIcon';
import AlertModal from '../AlertModal';
import AddTemplateGroupModal from './AddTemplateGroupModal';
import updateTemplateGroup from '../../requests/Assets/UpdateTemplateGroup';
import { logEvent } from '../LogEvent';
import Style from '../../constants/Style';
import { DELETE_TEMPLATE_GROUP_SUCCESS } from '../../constants/AlertMessages';
import PressableStyled, { PressableState } from '../PressableStyled';
import { UserPermission, TemplateGroup } from '../../constants/Types';

interface TemplateGroupMenuItemProps {
  group: TemplateGroup;
  selectedGroupId: string;
  onPress: () => void;
  contextMenuOpen: () => void;
  contextMenuClose: () => void;
  currentContextMenu: string;
  templateGroups: TemplateGroup[];
  update;
  showScreenAlert;
}

export default function TemplateSideBarRowV2({
  group, selectedGroupId, onPress, contextMenuOpen, contextMenuClose, currentContextMenu, update,
  templateGroups, showScreenAlert,
}: TemplateGroupMenuItemProps) {
  const { isAdmin, permissions }: { isAdmin: boolean, permissions: UserPermission[] } = useSelector(
    (state: { user: { isAdmin: boolean, permissions: UserPermission[] } }) => state.user,
  );
  const canEditTemplate: boolean = isAdmin
    || permissions?.includes(UserPermission.TEMPLATES_UPDATE);
  const canDeleteTemplate: boolean = isAdmin
    || permissions?.includes(UserPermission.TEMPLATES_DELETE);
  const [editGroupModalVisible, setEditGroupModalVisible] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);
  const [alertVisible, setAlertVisible] = useState<boolean>(false);
  const [alertError, setAlertError] = useState<boolean>(true);
  const [alertTitle, setAlertTitle] = useState<string>('');
  const [alertMessage, setAlertMessage] = useState<string | ReactElement>('');
  const [alertAction, setAlertAction] = useState<() => void>(null);
  const [alertActionTitle, setAlertActionTitle] = useState<string>(null);

  const showAlert = (
    title: string,
    message: string | ReactElement,
    error: boolean = true,
    action: () => void = null,
    actionTitle: string = '',
  ) => {
    setAlertTitle(title);
    setAlertMessage(message);
    setAlertError(error);
    setAlertAction(() => action);
    setAlertActionTitle(actionTitle);
    setLoading(false);
    setAlertVisible(true);
  };

  const deleteError = (errorText: string) => showAlert(
    'Error',
    errorText ?? 'Could not delete template group. Please try again later.',
  );

  const editTemplateGroup: () => void = () => {
    logEvent('TemplateGroupRowWeb', 'Edit_Group');
    contextMenuClose();
    setEditGroupModalVisible(!editGroupModalVisible);
  };

  const promptDeletion: () => void = () => {
    logEvent('TemplateGroupRowWeb', 'Delete_Group');
    showAlert(
      'Delete template group',
      <>
        {'Are you sure you want to delete the template group '}
        <Text style={Style.alertBoldText}>{group.Name}</Text>
      </>,
      true,
      deleteTemplateGroup,
      'Delete',
    );
  };

  const deleteTemplateGroup: () => void = () => {
    contextMenuClose();
    setLoading(true);
    updateTemplateGroup(null, group.ButtonPrototypeGroupId, true).then((res) => {
      if (res && !res.isError) {
        showScreenAlert(
          DELETE_TEMPLATE_GROUP_SUCCESS,
          <>
            <Text style={Style.alertBoldText}>{group.Name}</Text>
            {' was successfully deleted'}
          </>,
          false,
        );
        update(null, group.ButtonPrototypeGroupId, false, true);
      } else deleteError(res?.errorText);
    }).catch((error) => deleteError(error?.message))
      .finally(() => setLoading(false));
  };

  const selected: boolean = selectedGroupId === group.ButtonPrototypeGroupId;

  return <>
    <Pressable
      onPress={onPress}
      style={({ hovered, pressed }: PressableState) => [
        styles.container, hovered && Style.listItemHovered, pressed && Style.listItemPressed,
        selected && styles.containerSelected,
      ]}
    >
      <Text style={[styles.sectionTitle, selected && styles.sectionTitleSelected]}>
        {templateGroups?.filter((g: TemplateGroup) => g.ButtonPrototypeGroupId
          === group?.ButtonPrototypeGroupId)[0]?.Name}
      </Text>
      {(canEditTemplate || canDeleteTemplate) && <>
        <PressableStyled
          hitSlop={{ left: 40 }}
          style={styles.ellipsis}
          onPress={() => {
            logEvent('TemplateGroupRowWeb', 'Ellipsis');
            if (currentContextMenu === group.ButtonPrototypeGroupId) {
              contextMenuClose();
            } else contextMenuOpen();
          }}
          testID='am-template-group-menu-btn'
        >
          <EllipsisIcon
            fill={selected ? Colors.light.hereworksDarkBlue100 : Colors.light.hereworksDarkBlue400}
            style={{ marginRight: 18 }}
          />
        </PressableStyled>
        {currentContextMenu === group.ButtonPrototypeGroupId && <View style={styles.menu}>
          {canEditTemplate && <Pressable
            style={({ hovered, pressed }: PressableState) => [
              styles.contextItem,
              hovered && Style.listItemHovered, pressed && Style.listItemPressed,
            ]}
            onPress={editTemplateGroup}
          >
            <Text style={styles.contextItemText}>{'Edit'}</Text>
          </Pressable>}
          {canDeleteTemplate && <Pressable
            style={({ hovered, pressed }: PressableState) => [
              styles.contextItem,
              hovered && Style.listItemHovered, pressed && Style.listItemPressed,
            ]}
            onPress={promptDeletion}
          >
            <Text style={styles.contextItemText}>{'Delete'}</Text>
          </Pressable>}
        </View>}
      </>}
    </Pressable>
    <AddTemplateGroupModal
      templateGroups={templateGroups}
      isVisible={editGroupModalVisible}
      hide={() => setEditGroupModalVisible(false)}
      id={group.ButtonPrototypeGroupId}
      currentName={group.Name}
      update={update}
    />
    <AlertModal
      modalVisible={alertVisible}
      title={alertTitle}
      message={alertMessage}
      error={alertError}
      action={alertAction}
      actionTitle={alertActionTitle}
      {...{ loading }}
      setModalVisible={setAlertVisible}
    />
  </>;
}

const styles = StyleSheet.create({
  container: {
    ...Style.row,
    zIndex: -1,
    borderRadius: 8,
    marginHorizontal: 12,
    height: 46,
    padding: 12,
    paddingEnd: 18,
  },
  containerSelected: {
    backgroundColor: Colors.light.hereworksBlue500,
  },
  sectionTitle: {
    fontFamily: 'Poppins_400Regular',
    fontSize: 14,
    color: Colors.light.hereworksBlack500,
  },
  sectionTitleSelected: {
    color: '#FFF',
  },
  contextItem: {
    paddingVertical: 9,
    backgroundColor: '#fff',
  },
  contextItemText: {
    marginLeft: 12,
    color: Colors.light.hereworksPrimaryGrey,
    fontFamily: 'Poppins_400Regular',
    fontSize: 14,
    lineHeight: 21,
  },
  ellipsis: {
    position: 'absolute',
    right: 0,
    justifyContent: 'center',
    height: '100%',
  },
  menu: {
    zIndex: 5,
    position: 'absolute',
    top: 0,
    right: 50,
    width: 87,
    borderWidth: 1.5,
    borderColor: Colors.light.hereworksBlue100,
    borderRadius: 8,
    backgroundColor: '#fff',
    overflow: 'hidden',
  },
});
